import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { uomConversionAuditService } from 'companySettings/services';
import {
  IUomConversionAuditBulkSetUpResult,
  IUomConversionAuditBulkUpdateResult,
  IUomConversionAuditConversionPayload,
  IUomConversionAuditReport,
  IUomConversionAuditReportRequestPayload,
} from 'companySettings/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';

// The report is an unpaginated snapshot; without staleTime, every remount of the
// page refetches it and flashes the grid's loading overlay over correct data.
const STALE_TIME_MS = 5 * 60 * 1000;

export const useGetUomConversionAuditReport = (
  requestPayload: IUomConversionAuditReportRequestPayload
) => {
  // The key carries only the range: the report is scoped to the session company
  // server-side, and switching account or company is a full page load, which drops
  // this cache. An in-SPA company switch would have to add the company here, or
  // staleTime would serve the previous company's rows.
  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    ['uomConversionAuditReport', requestPayload],
    ({ signal }) => uomConversionAuditService.getUomConversionAuditReport(requestPayload, signal),
    {
      keepPreviousData: true,
      staleTime: STALE_TIME_MS,
    }
  );

  return {
    isError,
    isFetching,
    isLoading,
    refetch,
    report: data?.record as IUomConversionAuditReport,
  };
};

export const useBulkUpdateUoms = () => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    // Dynamic: the skipped count is the whole point of the message. Parts the
    // company has no catalogue entry for cannot take a UOM, and silently
    // reporting "47 updated" when 3 were untouched would hide that.
    // useHandleErrors calls success(variables, data) — the response is the SECOND
    // argument; the first is the payload we sent.
    success: (_payload: unknown, data: IUomConversionAuditBulkUpdateResult) => ({
      message: data?.skipped
        ? t('messageUomConversionAuditBulkUpdateSuccessWithSkipped', {
            count: data.updated,
            skipped: data.skipped,
          })
        : t('messageUomConversionAuditBulkUpdateSuccess', { count: data?.updated }),
    }),
    loading: { message: t('messageUomConversionAuditBulkUpdateInProgress') },
    error: { title: t('errorTitleUomConversionAuditBulkUpdate') },
    position: NotificationPosition.FORM,
    // The report's UOM columns are read straight off corporate_part, so the grid
    // is stale the moment this succeeds.
    invalidateQueries: ['uomConversionAuditReport'],
  });

  const { mutateAsync, isLoading } = useMutation(uomConversionAuditService.bulkUpdateUoms, {
    ...errors,
  });

  return { bulkUpdateUoms: mutateAsync, isBulkUpdating: isLoading };
};

export const useBulkSetUpParts = () => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    // Dynamic: one selection can create some entries and update others, and the
    // split is the point — "33 saved" would hide which parts were new.
    // success(variables, data) — the response is the second argument.
    success: (_payload: unknown, data: IUomConversionAuditBulkSetUpResult) => ({
      message: data?.skippedNoManufacturer
        ? t('messageUomConversionAuditBulkSetUpSuccessWithSkipped', {
            count: data.created,
            updated: data.updated,
            skipped: data.skippedNoManufacturer,
          })
        : t('messageUomConversionAuditBulkSetUpSuccess', {
            count: data?.created ?? 0,
            updated: data?.updated ?? 0,
          }),
    }),
    loading: { message: t('messageUomConversionAuditBulkSetUpInProgress') },
    error: { title: t('errorTitleUomConversionAuditBulkSetUp') },
    position: NotificationPosition.FORM,
    // Saving the entries changes every column the report reads for those rows.
    invalidateQueries: ['uomConversionAuditReport'],
  });

  const { mutateAsync } = useMutation(uomConversionAuditService.bulkSetUpParts, { ...errors });

  return { bulkSetUpParts: mutateAsync };
};

export const useBulkUpsertUomConversions = () => {
  const { t } = useTranslation();

  const { errors } = useHandleErrors({
    // success(variables, data): the count comes from what we sent, since the
    // entity endpoint echoes the saved records rather than a count.
    success: (payload: IUomConversionAuditConversionPayload[]) => ({
      message: t('messageUomConversionAuditBulkConversionSuccess', { count: payload?.length }),
    }),
    loading: { message: t('messageUomConversionCreatingInProgress') },
    error: { title: t('errorTitleUomConversionCreating') },
    position: NotificationPosition.FORM,
    // The conversions decide the report's Missing/Exists split, so the rows are
    // stale the moment this succeeds.
    invalidateQueries: ['uomConversionAuditReport'],
  });

  const { mutateAsync } = useMutation(uomConversionAuditService.bulkUpsertUomConversions, {
    ...errors,
  });

  return { bulkUpsertUomConversions: mutateAsync };
};
